import { InnerBox } from "@/components/elements/InnerBox";
import { TalentListTable } from "@/features/talent/components/talent-list/TalentListTable";
import { TalentListTop } from "@/features/talent/components/talent-list/TalentListTop";
import { divider } from "@/theme/colors";
import { Stack, Typography } from "@mui/material";
import { FC, useState, useEffect, useMemo, useCallback } from "react";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { usePagination } from "@/hooks/usePagination";
import { deleteTalentApi, exportTalentApi, useTalentList } from "@/api/services/main/talent";
import { Pagination } from "@/components/elements/Pagination";
import { Button } from "@/components/elements/Button";
import { useDialog } from "@/hooks/useDialog";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import { TalentCannotDeleteModal } from "@/features/talent/components/talent-common/TalentCannotDeleteModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TGetTalentListReqDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";
import { IconActionDelete } from "@/assets/icons";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { exportExcel } from "@/features/talent/utils/excel";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { useMe } from "@/api/services/main/auth";

type TTalentListPageProps = {};

type TTalentFilterForm = {
  keyword: string;
  nationalityId: number;
  companySpecificIndustryId: number;
  recruitmentStatus: number;
};

const defaultValues: TTalentFilterForm = {
  keyword: "",
  nationalityId: 0,
  companySpecificIndustryId: 0,
  recruitmentStatus: 0,
};

const STORAGE_KEY = "talent_selected";
const STORAGE_KEY_SELECT_ALL = "talent_select_all_flag";

export const TalentListPage: FC<TTalentListPageProps> = () => {
  const pagReq = usePagination();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState<TTalentFilterForm>({ ...defaultValues });
  const [filters, setFilters] = useState<TTalentFilterForm>({ ...defaultValues });
  const [searchParams] = useSearchParams();
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [selectionUpdateTrigger, setSelectionUpdateTrigger] = useState<number>(0);
  const [_isExport, setIsExport] = useState<boolean>(false);

  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { data: meResult } = useMe();

  const query: TGetTalentListReqDto = {
    page: pagReq.page || START_PAGE,
    perPage: pagReq.perPage || DEFAULT_PER_PAGE,
    ...filters,
  };

  if (searchParams.get("sorts")) {
    query.sorts = searchParams.get("sorts") ? [searchParams.get("sorts") as `${string}|${string}`] : [];
  }

  const { data, isLoading } = useTalentList(query);

  const confirmDelete = useDialog();
  const errorDelete = useDialog();
  const [talentNamesCannotDelete, setTalentNamesCannotDelete] = useState<string[]>([]);

  // Helper functions for localStorage
  const getTalentSelected = () => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  };

  const getSelectAllFlag = () => {
    const raw = localStorage.getItem(STORAGE_KEY_SELECT_ALL);
    if (!raw) return false;
    try {
      return JSON.parse(raw);
    } catch {
      return false;
    }
  };

  const clearTalentSelected = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_SELECT_ALL);
  };

  // Clear selection when filters change
  useEffect(() => {
    clearTalentSelected();
    setIsSelectAll(false);
    setSelectionUpdateTrigger((prev) => prev + 1);
  }, [filters]);

  // Load isSelectAll flag from localStorage on mount and page change
  useEffect(() => {
    const savedFlag = getSelectAllFlag();
    setIsSelectAll(savedFlag);
  }, [pagReq.page]);

  // Get current page selected IDs from localStorage
  const currentPageSelectedIds = useMemo(() => {
    const dataSelected = getTalentSelected();
    const currentPage = pagReq.page || START_PAGE;
    return (dataSelected[currentPage] as number[]) || [];
  }, [data?.data, pagReq.page, selectionUpdateTrigger]);

  // Check if delete button should be enabled
  const isDeleteEnabled = useMemo(() => {
    if (isSelectAll) return true;
    const dataSelected = getTalentSelected();
    const talentIds = Object.values(dataSelected).flat() as number[];
    return talentIds.length > 0;
  }, [isSelectAll, selectionUpdateTrigger, pagReq.page]);

  // Handle selection change from table
  const handleSelectionChange = (selectedIds: number[], isAllSelected: boolean) => {
    const currentPage = pagReq.page || START_PAGE;
    const dataSelected = getTalentSelected();

    // Note: if isAllSelected=true, selectedIds contains UNCHECKED items (excluded)
    // if isAllSelected=false, selectedIds contains CHECKED items (selected)
    dataSelected[currentPage] = selectedIds;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataSelected));

    localStorage.setItem(STORAGE_KEY_SELECT_ALL, JSON.stringify(isAllSelected));
    setIsSelectAll(isAllSelected);
    setSelectionUpdateTrigger((prev) => prev + 1);
  };

  const handleClickExport = async (isExport: boolean) => {
    if (!isExport) return;
    const dataSelected = getTalentSelected();
    const talentIds = Object.values(dataSelected).flat() as number[];
    await handleExport(talentIds);
  };

  const handleExport = async (talentIds: number[]) => {
    if (!isSelectAll && talentIds.length === 0) return;
    try {
      setIsExport(true);
      const rs = await exportTalentApi(talentIds, isSelectAll, {
        keyword: query.keyword,
        nationalityId: query.nationalityId,
        companySpecificIndustryId: query.companySpecificIndustryId,
        recruitmentStatus: query.recruitmentStatus,
      });
      const listDataExport = rs.data;
      const matchName = meResult?.data?.match?.matchName ?? "";
      await exportExcel(listDataExport, companySpecificIndustryOptions, matchName);
    } catch (e) {}
    setIsExport(false);
  };

  const handleDelete = async () => {
    const dataSelected = getTalentSelected();
    const talentIds = Object.values(dataSelected).flat() as number[];

    // If not select all mode and no items selected, return
    if (!isSelectAll && talentIds.length === 0) return;

    const currentPageTalents = data?.data ?? [];
    let selectedTalentsFromCurrentPage: typeof currentPageTalents = [];

    if (isSelectAll) {
      const excludedIds = talentIds;
      selectedTalentsFromCurrentPage = currentPageTalents.filter((talent) => !excludedIds.includes(talent.id));
    } else {
      selectedTalentsFromCurrentPage = currentPageTalents.filter((talent) => talentIds.includes(talent.id));
    }

    const talentsWithCaseMatches = selectedTalentsFromCurrentPage.filter((talent) => talent.caseMatchStatus && talent.caseMatchStatus > -1);

    if (talentsWithCaseMatches.length > 0) {
      const talentNames = talentsWithCaseMatches.map((talent) => talent.fullName || "").filter(Boolean);
      setTalentNamesCannotDelete(talentNames);
      await errorDelete.show();
      return;
    }

    setIsSubmit(true);
    try {
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) {
        setIsSubmit(false);
        return;
      }

      const rs = await deleteTalentApi(talentIds, isSelectAll, {
        keyword: query.keyword,
        nationalityId: query.nationalityId,
        companySpecificIndustryId: query.companySpecificIndustryId,
        recruitmentStatus: query.recruitmentStatus,
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      clearTalentSelected();
      setIsSelectAll(false);
      setSelectionUpdateTrigger((prev) => prev + 1);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TALENT.FETCH_LIST] });
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmit(false);
    }
  };

  const navigateFirstPage = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set("page", "1");
    navigate(`/talent?${params.toString()}`);
  }, [navigate]);

  const onSubmit = useCallback(
    (vals: TTalentFilterForm) => {
      setFilters(vals);
      navigateFirstPage();
    },
    [navigateFirstPage],
  );

  useEffect(() => {
    const isFormValuesEmpty =
      !formValues.keyword && !formValues.nationalityId && !formValues.companySpecificIndustryId && !formValues.recruitmentStatus;

    const isFiltersEmpty = !filters.keyword && !filters.nationalityId && !filters.companySpecificIndustryId && !filters.recruitmentStatus;

    if (isFormValuesEmpty && !isFiltersEmpty) {
      onSubmit(defaultValues);
    }
  }, [formValues, filters, onSubmit]);

  return (
    <>
      <Stack minHeight="100%">
        <Stack mb={6} justifyContent="space-between" alignItems="center" direction="row" width="100%">
          <Typography variant="head24Bold">人材一覧</Typography>
        </Stack>
        <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
          <Stack>
            {/* Search and Filter Section */}
            <TalentListTop
              formValues={formValues}
              onChangeFormValues={setFormValues}
              onSubmit={(vals) => onSubmit(vals)}
              pagination={data?.pagination}
              setIsExport={handleClickExport}
            />

            {/* Data Table */}
            <TalentListTable
              rows={data?.data ?? []}
              isLoading={isLoading}
              onSelectionChange={handleSelectionChange}
              selectedIds={currentPageSelectedIds}
            />

            <Stack mt={3} direction="row" justifyContent="space-between" alignItems="center">
              <Pagination totalPage={data?.pagination?.totalPage ?? 1} />
              <Button
                variant="danger"
                size="sm"
                color="error"
                disabled={isSubmit || !isDeleteEnabled}
                onClick={handleDelete}
                sx={{ width: "88px", mr: 4 }}
                startIcon={<IconActionDelete />}
              >
                <Typography variant="body14Semi">削除</Typography>
              </Button>
            </Stack>
          </Stack>
        </InnerBox>
      </Stack>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        onOk={confirmDelete.confirm}
        onClose={confirmDelete.cancel}
        message="人材情報を削除しますがよろしいですか？この操作は元に戻せませないため、ご注意ください。"
        title=""
      />

      <TalentCannotDeleteModal open={errorDelete.open} onClose={errorDelete.cancel} talentNames={talentNamesCannotDelete} />
    </>
  );
};
